import React, { useState, useEffect } from "react";
import { Table, Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { PinFill } from "react-bootstrap-icons";
import moment from "moment";

const topDevices = [
  "3030f94c7e84",
  "7c2c67ef20d8",
]

const TooltipedTime = ({ time }) => {
  const tzd = new Date().getTimezoneOffset() // Timezone difference in minutes
  // If time is more than 30 days ago, show the date instead of the time
  const diff = moment().diff(moment(time), "days")
  if (diff > 30) {
    return <span>{moment(time).format("YYYY-MM-DD HH:mm:ss")} (UTC)</span>
  }
  const tz_time = moment(time)
    .subtract(tzd, "minutes")
    .format("YYYY-MM-DD HH:mm:ss"); // Subtracting timezone difference to get local time
  const renderTooltip = (props) => (
    <Tooltip id="tooltip-id" {...props}>
      {tz_time}
    </Tooltip>
  );

  return (
    <OverlayTrigger
      placement="right"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip}
    >
      <span>{moment(tz_time).fromNow()}</span>
    </OverlayTrigger>
  );
};

const DevicesTable = ({ devices, updateFiles, onVersionChange, config }) => {
  const [localDevices, setLocalDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [recommendedFirmware, setRecommendedFirmware] = useState(
    config?.recommended_firmware_version || ""
  );
  const [filteredId, setFilteredId] = useState("");
  const [sortField, setSortField] = useState("device_id");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    setRecommendedFirmware(config?.recommended_firmware_version);
  }, [config]);

  useEffect(() => {
    // Device with ID "3030f94c7e84" is a test device and should be always shown at the top
    const testDevices = devices.filter((d) => topDevices.includes(d.device_id));
    let orderedDevices = devices;

    if (testDevices.length > 0) {
      const devicesWithoutTest = devices.filter(
        (d) => !topDevices.includes(d.device_id)
      );
      orderedDevices = [...testDevices, ...devicesWithoutTest];
    }
    setLocalDevices(orderedDevices);
  }, [devices]);

  useEffect(() => {
    let filtered = localDevices.filter((device) =>
      device.device_id.toUpperCase().includes(filteredId.toUpperCase())
    );

    // Separate top devices and regular devices
    const topFilteredDevices = filtered.filter(device => topDevices.includes(device.device_id));
    const regularFilteredDevices = filtered.filter(device => !topDevices.includes(device.device_id));

    // Sort regular devices
    regularFilteredDevices.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle date fields
      if (sortField.includes("_at")) {
        aValue = moment(aValue || 0).valueOf();
        bValue = moment(bValue || 0).valueOf();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    // Combine top devices with sorted regular devices
    setFilteredDevices([...topFilteredDevices, ...regularFilteredDevices]);
  }, [localDevices, filteredId, sortField, sortDirection]);

  const handleChange = (deviceId, selectedVersion) => {
    const updatedDevices = localDevices.map((device) => {
      if (device.device_id === deviceId) {
        return {
          ...device,
          recommended_firmware_version: selectedVersion,
        };
      }
      return device;
    });
    setLocalDevices(updatedDevices);
  };

  const onConfirm = (deviceId) => {
    const device = localDevices.find((d) => d.device_id === deviceId);
    onVersionChange(device.device_id, device.recommended_firmware_version);
  };

  const handleSortChange = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div>
      <h1>Devices ({localDevices.length})</h1>
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <input
          type="text"
          placeholder="Search by id..."
          value={filteredId}
          onChange={(e) => setFilteredId(e.target.value)}
          style={{
            borderRadius: "5px",
            padding: "5px",
            border: "1px solid lightgray",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "5px", marginLeft: "10px" }}>
          <div style={{ marginTop: "5px" }}>
            Sort by:
          </div>
          <Form.Select
            value={sortField}
            onChange={(e) => handleSortChange(e.target.value)}
            style={{ width: "150px" }}
          >
            <option value="device_id">Device ID</option>
            <option value="firmware_version">Current Firmware</option>
            <option value="device_firmware_file_requested_at">Last Firmware Info</option>
            <option value="device_recommended_version_requested_at">Last Update Request</option>
            <option value="first_seen_at">Created At</option>
            <option value="updated_at">Modified At</option>
          </Form.Select>
          <Button
            variant="outline-secondary"
            onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
          >
            {sortDirection === "asc" ? "↑" : "↓"}
          </Button>
        </div>
      </div>
      <Table striped bordered hover size="sm" className="text-center">
        <thead>
          <tr>
            <th>#</th>
            <th>Device ID</th>
            <th>Current firmware</th>
            <th>Firmware to update</th>
            <th>Last firmware info request</th>
            <th>Last update file request</th>
            <th>Created at</th>
            <th>Modified at</th>
            <th>Last seen</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredDevices.map((device, index) => (
            <tr key={device.device_id}>
              <td>{index + 1}</td>
              <td>
                {device.device_id}
                {topDevices.includes(device.device_id) && (
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<Tooltip>Pinned to top</Tooltip>}
                  >
                    <PinFill className="ms-2" style={{ color: "#0d6efd" }} />
                  </OverlayTrigger>
                )}
              </td>
              <td
                style={{
                  backgroundColor:
                    device.firmware_version ===
                    (devices.filter((d) => d.device_id === device.device_id)[0]
                      .recommended_firmware_version === "recommended"
                      ? recommendedFirmware
                      : devices.filter(
                          (d) => d.device_id === device.device_id
                        )[0].recommended_firmware_version)
                      ? "lightgreen"
                      : "lightcoral",
                }}
              >
                {device.firmware_version}
              </td>
              <td>
                <Form.Select
                  value={device.recommended_firmware_version || ""}
                  onChange={(e) =>
                    handleChange(device.device_id, e.target.value)
                  }
                >
                  <option disabled value="">
                    Select firmware
                  </option>
                  <option value="recommended">
                    Recommended ({recommendedFirmware})
                  </option>
                  {updateFiles.map((file) => (
                    <option key={file.version} value={file.version}>
                      {file.version}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <TooltipedTime
                  time={device.device_firmware_file_requested_at}
                />
              </td>
              <td>
                {device.device_recommended_version_requested_at ? (
                  <TooltipedTime
                    time={device.device_recommended_version_requested_at}
                  />
                ) : (
                  "N/A"
                )}
              </td>
              <td>
                <TooltipedTime time={device.first_seen_at} />
              </td>
              <td>
                <TooltipedTime time={device.updated_at} />
              </td>
              {/* Last seen is a most recent time from device_firmware_file_requested_at and device_recommended_version_requested_at */}
              <td>
                <TooltipedTime
                  time={moment.max([
                    moment(device.device_firmware_file_requested_at || 0),
                    moment(device.device_recommended_version_requested_at || 0),
                  ])}
                />
              </td>
              <td>
                {device.recommended_firmware_version !==
                  devices.filter((d) => d.device_id === device.device_id)[0]
                    .recommended_firmware_version && (
                  <Button
                    variant="primary"
                    onClick={() => onConfirm(device.device_id)}
                  >
                    Confirm
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default DevicesTable;
